import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {Animated, TouchableOpacity, View, StyleSheet, Image} from 'react-native';
import {ViewProps} from './model';
import {Text} from '../../../../shared/components/text';
import {images} from '../../../../assets/icons';
import {useStyles} from '../../../../shared/hooks/styles';
import {useTheme} from '../../../../shared/theme';
import {Link} from '../../../../shared/components/link';
import {formatPhone, formatPhoneToCallable, openExternalPhone} from '../../../../shared/pipes';
import {DeviceSize} from '../../../../shared/theme/helpers';
import {useIsDesktop} from '../../../../shared/hooks/useIsDesktop';
import {Map} from '../../../../shared/components/map';
import {hexToRgbA} from '../../../../shared/pipes';
import {GeoMapBlurredDecoration} from '../../../../assets/images';

const AnimatedView = Animated.View;

export default ({acceptorBranches, activeAcceptorBranch, onClickAcceptorBranch, acceptor}: ViewProps) => {
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const opacity = useRef(new Animated.Value(1)).current;

  const styles = useStyles(({addShadow, mediaOnly}) => ({
    container: {
      ...mediaOnly(
        DeviceSize.desktop,
        {
          borderRadius: 40,
          overflow: 'hidden',
          flexDirection: 'row',
          alignItems: 'stretch',
          ...addShadow({
            opacity: 0.1,
            width: 0,
            height: 2,
            radius: 18,
          }),
        },
        {
          borderRadius: 32,
          overflow: 'hidden',
        },
      ),
    },
    contentBackdrop: {
      position: 'absolute',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      backgroundColor: hexToRgbA('#000', 0.2),
      zIndex: 1,
    },
    content: {
      backgroundColor: theme.color.primaryNonGreyBackground,
      position: 'absolute',
      overflow: 'hidden',
      zIndex: 2,
      ...mediaOnly(
        DeviceSize.desktop,
        {
          width: 310,
          borderTopLeftRadius: 5,
          borderBottomLeftRadius: 5,
          paddingBottom: 120,
          paddingTop: 80,
          paddingHorizontal: 26,
          left: 0,
          bottom: 0,
          top: 0,
          ...addShadow({
            opacity: 0.1,
            width: 0,
            height: 2,
            radius: 18,
          }),
        },
        {
          left: 20,
          right: 20,
          top: 20,
          borderRadius: 24,
          paddingHorizontal: 16,
          paddingTop: 20,
          backgroundColor: theme.color.primaryNonGreyBackground,
        },
      ),
    },
    map: mediaOnly(DeviceSize.desktop, {
      minHeight: 650,
      flexShrink: 1,
      flexGrow: 1,
    }),
    group: {
      marginBottom: 40,
    },
    groupTitle: {
      color: theme.color.textColor,
      marginBottom: 11,
      ...mediaOnly(
        DeviceSize.desktop,
        {
          ...theme.fonts.h5,
        },
        {
          ...theme.fonts.h6,
        },
      ),
    },
    groupItem: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      marginBottom: 12,
      paddingVertical: 5,
    },
    groupItemIcon: {
      marginRight: 10,
      flexShrink: 0,
      color: theme.color.primaryColor,
      width: 24,
      height: 24,
    },
    groupItemLabel: {
      ...theme.fonts.h5Light,
      lineHeight: 24,
      flex: 1,
      ...mediaOnly(
        DeviceSize.desktop,
        {
          ...theme.fonts.h5Light,
        },
        {
          ...theme.fonts.bodySmallLight,
        },
      ),
    },
    closeButton: {
      position: 'absolute',
      zIndex: 2,
      alignItems: 'center',
      justifyContent: 'center',
      ...mediaOnly(
        DeviceSize.desktop,
        {
          width: 44,
          height: 44,
          right: 15,
          top: 15,
          color: theme.color.textColor,
        },
        {
          width: 24,
          height: 24,
          right: 16,
          top: 16,
          color: theme.color.textColor,
        },
      ),
    },
    closeButonIcon: mediaOnly(
      DeviceSize.desktop,
      {
        width: 24,
        height: 24,
        color: theme.color.gray600,
      },
      {
        width: 24,
        height: 24,
        color: theme.color.gray600,
      },
    ),
    closeButtonTouchable: {width: '100%', flex: 1},
    bottomDecoration: {
      position: 'absolute',
      left: -16,
      bottom: 0,
      width: 351,
      height: 226,
    },
  }));

  useEffect(() => {
    Animated.timing(opacity, {useNativeDriver: true, toValue: activeAcceptorBranch ? 1 : 0}).start();
  }, [activeAcceptorBranch]);

  const centerPoint: number[] = useMemo(
    () => (acceptorBranches[0]?.geolocation?.length === 2 ? acceptorBranches[0].geolocation : [68.95912, 33.072544]), // [68.95912, 33.072544] is Murmansk city
    [acceptorBranches],
  );

  const markers = useMemo(
    () =>
      acceptorBranches
        .filter((ab) => ab.geolocation?.length === 2)
        .map((item) => ({
          payload: item,
          coordinates: item.geolocation!,
        })),
    [acceptorBranches],
  );

  const handleClickMarker = useCallback((data) => onClickAcceptorBranch(data.payload), [onClickAcceptorBranch]);
  const handleNullacceptorBranch = useCallback(() => onClickAcceptorBranch(null), [onClickAcceptorBranch]);

  const closeButonIconProps = StyleSheet.flatten(styles.closeButonIcon);

  return (
    <View style={styles.container}>
      {!isDesktop && (
        <AnimatedView
          pointerEvents={activeAcceptorBranch ? 'auto' : 'none'}
          style={[styles.contentBackdrop, {opacity: opacity}]}>
          <TouchableOpacity
            testID='not-desktop-acceptor-click-id'
            style={styles.closeButtonTouchable}
            onPress={handleNullacceptorBranch}
          />
        </AnimatedView>
      )}
      <AnimatedView
        pointerEvents={isDesktop ? 'auto' : activeAcceptorBranch ? 'auto' : 'none'}
        style={[styles.content, {opacity: opacity}]}>
        <TouchableOpacity testID='all-accesptor-click-id' style={styles.closeButton} onPress={handleNullacceptorBranch}>
          <images.Close {...closeButonIconProps} />
        </TouchableOpacity>
        {activeAcceptorBranch && (
          <View>
            <View style={styles.group}>
              <Text style={styles.groupTitle}>{activeAcceptorBranch?.name || acceptor?.name || ' '}</Text>
              {activeAcceptorBranch.address && (
                <View style={styles.groupItem}>
                  <View style={styles.groupItemIcon}>
                    <images.Location width={24} height={24} color={styles.groupItemIcon.color} />
                  </View>
                  <Text style={styles.groupItemLabel}>{activeAcceptorBranch.address}</Text>
                </View>
              )}
              {activeAcceptorBranch.phone && (
                <Link testID='phone-geo-map-link-id' external style={styles.groupItem}>
                  <View style={styles.groupItemIcon}>
                    <images.Phone width={24} height={24} color={styles.groupItemIcon.color} />
                  </View>
                  <Text
                    onPress={() => openExternalPhone(formatPhoneToCallable(activeAcceptorBranch.phone))}
                    style={styles.groupItemLabel}>
                    {formatPhone(activeAcceptorBranch.phone)}
                  </Text>
                </Link>
              )}
            </View>
            {activeAcceptorBranch.operatingMode && (
              <View style={styles.group}>
                <Text style={styles.groupTitle}>Часы работы</Text>
                <View style={styles.groupItem}>
                  <View style={styles.groupItemIcon}>
                    <images.Clock2 width={24} height={24} color={styles.groupItemIcon.color} />
                  </View>
                  <Text style={styles.groupItemLabel}>{activeAcceptorBranch.operatingMode}</Text>
                </View>
              </View>
            )}
          </View>
        )}
        {isDesktop && <Image source={GeoMapBlurredDecoration as any} style={styles.bottomDecoration} />}
      </AnimatedView>
      <View
        style={[
          styles.map,
          !isDesktop
            ? {
                height: 464,
              }
            : {},
        ]}>
        <Map onClickMarker={handleClickMarker} centerPoint={centerPoint} markers={markers} />
      </View>
    </View>
  );
};
